<!--
  AscendQE: one-time-code verification dialog.

    const ok = await this.$refs.otp.verify();   // resolves true / false

  Sends a code via /core/data/otp-send; the code is delivered to the in-app
  notification centre (window.aqeNotify). Six single-digit inputs with
  auto-advance, paste, backspace and arrow navigation; expiry countdown,
  resend cooldown, and a 30 s lock after three wrong attempts.
-->
<template>
  <teleport to="#app">
    <oxd-dialog v-if="show" class="aqe-otp-dialog" @update:show="finish(false)">
      <div class="aqe-otp" @keydown.esc="finish(false)">
        <div class="aqe-otp__icon" aria-hidden="true">
          <i class="oxd-icon bi-shield-lock"></i>
        </div>
        <h3 id="aqe-otp-title">Verify it's you</h3>
        <p class="aqe-otp__lead">
          We sent a 6-digit code to your notifications
          <i class="oxd-icon bi-bell"></i>. It expires in
          <strong class="aqe-otp__time">{{ mmss(expires) }}</strong
          >.
        </p>

        <div
          class="aqe-otp__boxes"
          :class="{'is-error': !!error, 'is-shake': shake}"
          role="group"
          aria-labelledby="aqe-otp-title"
        >
          <input
            v-for="(d, i) in digits"
            :key="i"
            :ref="(el) => (boxes[i] = el)"
            :value="d"
            class="aqe-otp__box"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="1"
            :aria-label="`Digit ${i + 1} of 6`"
            :disabled="locked > 0 || busy"
            @input="onInput(i, $event)"
            @keydown="onKey(i, $event)"
            @paste.prevent="onPaste($event)"
            @focus="$event.target.select()"
          />
        </div>
        <p class="aqe-otp__msg" role="alert">
          <template v-if="locked > 0"
            >Too many attempts. Try again in {{ locked }}s.</template
          >
          <template v-else-if="error">{{ error }}</template>
        </p>

        <div class="aqe-otp__actions">
          <button
            type="button"
            class="aqe-otp__resend"
            :disabled="cooldown > 0 || busy"
            @click="send"
          >
            {{ cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code' }}
          </button>
          <span class="aqe-otp__spacer"></span>
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="finish(false)"
          />
          <oxd-button
            display-type="secondary"
            :label="busy ? 'Verifying…' : 'Verify'"
            :disabled="code.length !== 6 || busy || locked > 0"
            @click="submit"
          />
        </div>
      </div>
    </oxd-dialog>
  </teleport>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';

export default {
  name: 'OtpDialog',
  components: {'oxd-dialog': OxdDialog},

  setup() {
    return {http: new APIService(window.appGlobal.baseUrl, '')};
  },

  data() {
    return {
      show: false,
      digits: ['', '', '', '', '', ''],
      boxes: [],
      error: null,
      shake: false,
      busy: false,
      attempts: 0,
      expires: 0,
      cooldown: 0,
      locked: 0,
      resolver: null,
      timer: null,
    };
  },

  computed: {
    code() {
      return this.digits.join('');
    },
  },

  beforeUnmount() {
    clearInterval(this.timer);
  },

  methods: {
    verify() {
      this.show = true;
      this.digits = ['', '', '', '', '', ''];
      this.error = null;
      this.attempts = 0;
      this.locked = 0;
      clearInterval(this.timer);
      this.timer = setInterval(this.tick, 1000);
      this.send();
      return new Promise((resolve) => (this.resolver = resolve));
    },
    tick() {
      if (this.expires > 0) this.expires--;
      if (this.cooldown > 0) this.cooldown--;
      if (this.locked > 0 && --this.locked === 0) this.focus(0);
      if (this.expires === 0 && this.show && !this.error)
        this.error = 'This code has expired. Request a new one.';
    },
    mmss(s) {
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    },
    async send() {
      this.cooldown = 30;
      this.error = null;
      this.digits = ['', '', '', '', '', ''];
      try {
        const {data} = await this.http.request({
          method: 'POST',
          url: '/core/data/otp-send',
          data: {},
        });
        this.expires = data.expiresIn || 120;
        // simulate delivery latency, then drop it into the notification centre
        setTimeout(() => {
          if (window.aqeNotify) {
            window.aqeNotify(
              'Your verification code',
              'Use this code to confirm your password change. It expires in 2 minutes.',
              {code: data.code, kind: 'otp'},
            );
          }
        }, 900 + Math.random() * 1200);
      } catch (e) {
        this.error = 'Could not send a code. Try again.';
      }
      this.$nextTick(() => this.focus(0));
    },
    focus(i) {
      const el = this.boxes[Math.max(0, Math.min(5, i))];
      el && el.focus();
    },
    onInput(i, e) {
      const v = String(e.target.value).replace(/\D/g, '');
      if (!v) {
        this.digits[i] = '';
        e.target.value = '';
        return;
      }
      if (v.length > 1) return this.fill(v, i);
      this.digits[i] = v;
      this.error = null;
      if (i < 5) this.focus(i + 1);
      else if (this.code.length === 6) this.submit();
    },
    onKey(i, e) {
      if (e.key === 'Backspace' && !this.digits[i] && i > 0) {
        e.preventDefault();
        this.digits[i - 1] = '';
        this.focus(i - 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.focus(i - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.focus(i + 1);
      } else if (e.key === 'Enter' && this.code.length === 6) {
        this.submit();
      }
    },
    onPaste(e) {
      this.fill((e.clipboardData || window.clipboardData).getData('text'), 0);
    },
    fill(text, start) {
      const d = String(text)
        .replace(/\D/g, '')
        .slice(0, 6 - start)
        .split('');
      d.forEach((c, k) => (this.digits[start + k] = c));
      this.error = null;
      const next = start + d.length;
      if (this.code.length === 6) this.submit();
      else this.focus(next);
    },
    async submit() {
      if (this.busy || this.code.length !== 6 || this.locked > 0) return;
      if (this.expires === 0) {
        this.error = 'This code has expired. Request a new one.';
        return;
      }
      this.busy = true;
      try {
        const r = await this.http.request({
          method: 'POST',
          url: '/core/data/otp-verify',
          data: {code: this.code},
          validateStatus: (s) => s === 200 || s === 422,
        });
        if (r.status === 200 && r.data.verified) return this.finish(true);
        this.attempts++;
        this.error =
          this.attempts >= 3
            ? null
            : `That code is not right. ${3 - this.attempts} attempt${
                3 - this.attempts === 1 ? '' : 's'
              } left.`;
        if (this.attempts >= 3) {
          this.locked = 30;
          this.attempts = 0;
        }
        this.shake = true;
        setTimeout(() => (this.shake = false), 450);
        this.digits = ['', '', '', '', '', ''];
        this.$nextTick(() => this.focus(0));
      } finally {
        this.busy = false;
      }
    },
    finish(ok) {
      clearInterval(this.timer);
      this.show = false;
      if (this.resolver) this.resolver(ok);
      this.resolver = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.aqe-otp {
  width: min(420px, 82vw);
  text-align: center;
  font-family: Inter, 'Nunito Sans', sans-serif;
  h3 {
    margin: 0 0 6px;
    color: #0f172a;
    font-size: 19px;
    letter-spacing: -0.02em;
  }
}
.aqe-otp__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 12px;
  display: grid;
  place-items: center;
  border-radius: 16px;
  background: linear-gradient(135deg, #10b981, #6366f1);
  color: #fff;
  font-size: 22px;
}
.aqe-otp__lead {
  margin: 0 0 18px;
  color: #475569;
  font-size: 14px;
}
.aqe-otp__time {
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}
.aqe-otp__boxes {
  display: flex;
  justify-content: center;
  gap: 8px;
  &.is-shake {
    animation: aqe-shake 0.4s;
  }
}
@keyframes aqe-shake {
  20%,
  60% {
    transform: translateX(-6px);
  }
  40%,
  80% {
    transform: translateX(6px);
  }
}
.aqe-otp__box {
  width: 46px;
  height: 54px;
  border: 1px solid rgba(15, 23, 42, 0.14);
  border-radius: 12px;
  background: #fff;
  color: #0f172a;
  font: 700 22px Inter, sans-serif;
  text-align: center;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
  }
  .is-error & {
    border-color: #ef4444;
  }
  &:disabled {
    background: #f1f5f9;
  }
}
.aqe-otp__msg {
  min-height: 20px;
  margin: 10px 0 4px;
  color: #b91c1c;
  font-size: 13px;
}
.aqe-otp__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
}
.aqe-otp__spacer {
  flex: 1;
}
.aqe-otp__resend {
  border: 0;
  background: none;
  color: #6366f1;
  font: 600 13px Inter, sans-serif;
  cursor: pointer;
  &:disabled {
    color: #94a3b8;
    cursor: default;
  }
}
</style>
